import { Spinner, Link } from "@skbkontur/react-ui";
import * as React from "react";
import { useEffect, useRef } from "react";

import { declint } from "../../../../Engine/utils";
import { ErrorGroupHeader } from "../../EmbeddedLog/ErrorList/ErrorsGroup/ErrorGroupHeader";
import { GroupWrapper } from "../../EmbeddedLog/ErrorList/ErrorsGroup/ErrorsGroup.styles";
import {
    IconWrapper,
    ListItem,
    ListItemSource,
    ListItemWrapper,
    SourceItemLink,
    FakeIcon,
} from "../../EmbeddedLog/ErrorList/ErrorItem/ErrorsItem.styles";
import { ErrorItemIcon, ErrorItemIconStatus } from "../../EmbeddedLog/ErrorList/ErrorItem/ErrorItemIcon";

import { NdsSverkaPanel } from "./NdsSverkaPanel.styles";
import { NdsSverkaCheckResult } from "./NdsSverkaTypes";

export interface NdsSverkaCheckPanelProps {
    checkResult: NdsSverkaCheckResult;
    ndsSverkaRedirectUrl?: string;
    setAdditionalOffset(offset: number): void;
}

export const NdsSverkaCheckPanel = (props: NdsSverkaCheckPanelProps): React.ReactElement => {
    const ndsSverkaRef = useRef<HTMLDivElement>(null);

    const linkNdsPlus = (
        <ListItemSource>
            <Link href={props.ndsSverkaRedirectUrl} target="_blank">
                <SourceItemLink disabled data-tid="LinkViewNDSPlus">
                    Посмотреть в НДС+
                </SourceItemLink>
            </Link>
        </ListItemSource>
    );

    useEffect(() => {
        if (!ndsSverkaRef.current) {
            return undefined;
        }

        const observer = new ResizeObserver(entries => {
            const contentRect = entries?.[0]?.borderBoxSize?.[0];
            const height = contentRect?.blockSize ?? 0;

            props.setAdditionalOffset(height);
        });

        observer.observe(ndsSverkaRef.current);

        return () => {
            props.setAdditionalOffset(0);
            observer.disconnect();
        };
    }, []);

    function renderStatus() {
        if (props.checkResult.status === "loading") {
            return (
                <ListItem>
                    <IconWrapper />
                    <Spinner caption="Ищем расхождения" type="mini" />
                </ListItem>
            );
        } else if (props.checkResult.status === "noErrors") {
            return (
                <ListItem>
                    <IconWrapper>
                        <ErrorItemIcon status={ErrorItemIconStatus.Success} />
                    </IconWrapper>
                    <div>
                        Расхождений нет
                        {linkNdsPlus}
                    </div>
                </ListItem>
            );
        } else if (props.checkResult.status === "hasErrors") {
            return (
                <ListItem>
                    <IconWrapper>
                        <ErrorItemIcon status={ErrorItemIconStatus.Warning} />
                    </IconWrapper>
                    <div>
                        Нашли{" "}
                        {declint(props.checkResult.diffsCount || 0, [
                            "%s расхождение",
                            "%s расхождения",
                            "%s расхождений",
                        ])}
                        {linkNdsPlus}
                    </div>
                </ListItem>
            );
        } else if (props.checkResult.status === "ndsError") {
            return (
                <ListItem>
                    <IconWrapper>
                        <FakeIcon />
                    </IconWrapper>
                    <div>
                        {props.checkResult.errorMsg || "Ошибка при получении данных"}
                        {linkNdsPlus}
                    </div>
                </ListItem>
            );
        } else {
            return (
                <ListItem>
                    <IconWrapper>
                        <FakeIcon />
                    </IconWrapper>
                    <div>{props.checkResult.errorMsg || "Ошибка при получении данных"}</div>
                </ListItem>
            );
        }
    }

    return (
        <NdsSverkaPanel ref={ndsSverkaRef}>
            <GroupWrapper data-tid={"protocol-nds-sverka"}>
                <ErrorGroupHeader
                    tid={"protocol-nds-sverka-header"}
                    title={"Проверка расхождений с контрагентами"}
                    noWrap
                />
                <ListItemWrapper>{renderStatus()}</ListItemWrapper>
            </GroupWrapper>
        </NdsSverkaPanel>
    );
};
